<template>
	<div>
		<app-loader v-if="loading" size="large"></app-loader>

		<app-alert v-else-if="stock && displayItems.length === 0">
			There are no items to display
		</app-alert>

		<div v-else-if="stock">
		<!-- Request Stock Shortage Button (hidden in compact mode) -->
		<div v-if="!compact && showShortageButton && canRequestShortage" class="text-right mb-3">
			<app-button
				variant="primary"
				size="small"
				@click="emitShortageRequest">
				Request Stock Shortage
			</app-button>
		</div>

		<!-- Filters (hidden in compact mode) -->
		<div v-if="!compact" class="bg-white rounded-lg border border-gray-200 p-3 mb-3 space-y-3">
			<!-- Stock Status Filter -->
			<div>
				<div class="text-xs font-medium text-gray-700 mb-2">Filter by Stock Status:</div>
				<div class="flex flex-wrap gap-2">
					<button
						v-for="option in stockStatusOptions"
						:key="option.id"
						:class="[
							'px-3 py-1.5 text-xs rounded-md border transition-all',
							stockStatuses.includes(option.id)
								? 'bg-mabey-600 text-white border-mabey-600'
								: 'bg-white text-gray-700 border-gray-300 hover:border-mabey-400'
						]"
						@click="toggleStockStatus(option.id)">
						<font-awesome-icon
							:icon="stockStatuses.includes(option.id) ? 'fas fa-check-square' : 'far fa-square'"
							class="mr-1" />
						{{ option.name }}
					</button>
				</div>
			</div>

			<!-- Search Filter -->
			<div>
				<div class="relative">
					<input
						v-model="itemSearchFilter"
						type="text"
						placeholder="Search by item code or description..."
						class="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mabey-400 focus:border-mabey-400" />
					<font-awesome-icon icon="fas fa-search" class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
				</div>
			</div>
		</div>

		<!-- Stock List -->
		<div v-if="compact" class="overflow-x-auto">
			<table class="w-full text-xs">
				<thead class="sticky top-0 bg-white">
					<tr class="bg-purple-50 border-b border-purple-200">
						<th class="px-2 py-1.5 text-left font-semibold text-gray-700 uppercase tracking-wide whitespace-nowrap sticky left-0 bg-purple-50 border-r border-purple-200">
							Item
						</th>
						<th v-for="depot in visibleDepots" :key="depot.depot" class="px-2 py-1.5 text-center font-semibold text-purple-700 uppercase tracking-wide whitespace-nowrap">
							{{ depot.depot }}
						</th>
					</tr>
				</thead>
				<tbody class="divide-y divide-gray-100">
					<tr v-for="item in displayItems" :key="item.item_code" class="hover:bg-gray-50 transition-colors">
						<td class="px-2 py-1.5 font-medium text-gray-900 whitespace-nowrap sticky left-0 bg-white border-r border-gray-200">
							{{ item.item_code }}
						</td>
						<td v-for="depot in visibleDepots" :key="depot.depot" class="px-2 py-1.5 text-center">
							<span class="font-bold" :class="getStockColorClass(item.item_code, item.quantity_required, depot.depot)">
								{{ getStockValue(item.item_code, depot.depot) }}
							</span>
						</td>
					</tr>
					<tr v-if="displayItems.length === 0">
						<td :colspan="visibleDepots.length + 1" class="px-4 py-8 text-center text-gray-500">
							No items to display
						</td>
					</tr>
				</tbody>
			</table>
		</div>
		<div v-else class="overflow-x-auto w-full">
			<app-alert v-if="displayItems.length === 0">
				There are no items to display
			</app-alert>

			<div v-else class="inline-block min-w-full">
				<div class="flex text-sm mt-4 p-1">
					<div class="flex-1 min-w-[310px] font-bold">ITEM</div>
					<div class="min-w-[42px] font-bold text-center">QTY</div>
					<template v-for="depot in visibleDepots" :key="depot.depot">
						<div
							class="min-w-[42px] w-[42px] mx-1 text-center flex flex-col justify-center">
							<span class="font-bold">{{ depot.depot }}</span>
							<span class="text-xs" :class="depot.distance_unknown ? 'text-gray-400' : ''">
									{{ depot.distance_unknown ? '' : formatDistance(depot.distance_value) }}
								</span>
						</div>
					</template>
				</div>
				<template v-for="item in displayItems" :key="item.item_code">
					<div class="flex text-xs p-1 whitespace-nowrap">
						<div class="flex-1 overflow-hidden text-ellipsis min-w-[310px]">
							{{ item.item_code }} {{ item.description }}
						</div>
						<div class="min-w-[42px] flex-shrink-0 text-center">{{ item.quantity_required }}</div>
						<template v-for="depot in visibleDepots" :key="depot.depot">
							<div
								class="mx-1 min-w-[42px] flex-shrink-0 py-0.5 rounded-md text-center"
								:class="
									depotStockClass(item.item_code, item.quantity_required, depot.depot)
								">
								{{ getStockValue(item.item_code, depot.depot) }}
							</div>
						</template>
					</div>
				</template>
			</div>
		</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { ref, computed, watch } from 'vue'
	import useAuthStore from '@/stores/auth'
	import { getPickingList } from '@/utils/contract-items'
	import StockService from '@/services/stock'
	import type { ContractItem } from '@/data/schema/contract-item'
	import type { Contract } from '@/data/schema/contract'
	import AppAlert from '@/components/base/AppAlert.vue'
	import useToaster from '@/stores/toaster'

	export interface StockListItem {
		item_code: string
		description: string
		quantity_required: number
	}

	const props = withDefaults(
		defineProps<{
			items?: ContractItem[]
			contract?: Contract
			stockItems?: StockListItem[]
			postcode?: string
			showShortageButton?: boolean
			compact?: boolean
		}>(),
		{
			items: () => [],
			contract: undefined,
			stockItems: () => [],
			postcode: '',
			showShortageButton: true,
			compact: false
		}
	)

	const emit = defineEmits<{
		shortageRequest: []
	}>()

	const depots = ref([])

	const stockStatusOptions = ref([
		{ id: 'quantity_available', name: 'Available' },
		{ id: 'quantity_service', name: 'In Service' },
		{ id: 'quantity_repair', name: 'In Repair' },
		{ id: 'quantity_allocated', name: 'Allocated' }
	])

	const stockStatuses = ref(['quantity_available'])
	const itemSearchFilter = ref<string>('')

	const toggleStockStatus = (statusId: string) => {
		const index = stockStatuses.value.indexOf(statusId)
		if (index > -1) {
			stockStatuses.value.splice(index, 1)
		} else {
			stockStatuses.value.push(statusId)
		}
	}

	const toaster = useToaster()
	const authStore = useAuthStore()

	const canRequestShortage = computed(() => {
		return authStore.isAbleTo(['can-raise-stock-shortage-requests'])
	})

	const pickingList = ref([])

	const stockApi = new StockService()

	const stock = ref(undefined)
	const depotDistances = ref<Array<{ id: number; origin_depot_key: string; destination_depot_key: string; distance_miles: string }>>([])

	const loading = ref(false)

	// Unified display items: use stockItems if provided, otherwise derive from ContractItem[] via getPickingList
	const displayItems = computed(() => {
		let items = []
		if (props.stockItems && props.stockItems.length > 0) {
			items = props.stockItems
		} else {
			items = pickingList.value
		}

		// Apply search filter
		if (itemSearchFilter.value) {
			const search = itemSearchFilter.value.toLowerCase()
			items = items.filter(item => {
				const matchesCode = item.item_code.toLowerCase().includes(search)
				const matchesDesc = item.description?.toLowerCase().includes(search)
				return matchesCode || matchesDesc
			})
		}

		return items
	})

	const visibleDepots = computed(() => depots.value)

	const loadStock = async () => {
		console.log('loadStock called')
		loading.value = true
		let itemCodes: string[] = []

		if (props.stockItems && props.stockItems.length > 0) {
			// Generic mode: use stockItems directly
			itemCodes = props.stockItems.map((item) => item.item_code)
		} else {
			// Legacy mode: derive from ContractItem[] via getPickingList
			pickingList.value = getPickingList(props.items, false, ['Unallocated', 'Quote'])
			console.log('pickingList length:', pickingList.value.length)
			itemCodes = pickingList.value.map((item) => item.item_code)
		}

		if (itemCodes.length > 0) {
			try {
				// Load stock data and depot distances matrix in parallel
				const [stockResponse, distancesResponse] = await Promise.all([
					stockApi.getDepotStock(itemCodes),
					stockApi.getDepotDistances()
				])

				stock.value = stockResponse.data.data
				depotDistances.value = distancesResponse.data?.data || distancesResponse.data || distancesResponse

				// Apply distances from the matrix using the contract's hire depot as the origin
				const contractDepot = props.contract?.hire_depot || props.contract?.depot

				// Extract unique depot abbreviations directly from stock data, including the contract's own depot
				const depotKeys = [...new Set((stock.value as any[]).map((s: any) => s.depot).filter(Boolean))]
				if (contractDepot && Array.isArray(depotDistances.value)) {
					const distancesFromContract = depotDistances.value.filter(
						(d: any) => d.origin_depot_key === contractDepot
					)
					depots.value = depotKeys.map((key) => {
						if (key === contractDepot) {
							return { depot: key, distance_value: 0, distance_unknown: false }
						}

						const distanceInfo = distancesFromContract.find(
							(d: any) => d.destination_depot_key === key
						)
						const distanceMiles = distanceInfo ? parseFloat(distanceInfo.distance_miles) : undefined
						return { depot: key, distance_value: distanceMiles, distance_unknown: distanceMiles === undefined }
					})
					depots.value.sort((a, b) => (a.distance_value ?? Infinity) - (b.distance_value ?? Infinity))
				} else {
					// No contract depot — sort alphabetically, no distance shown
					depots.value = depotKeys.map((key) => ({ depot: key, distance_value: undefined, distance_unknown: true })).sort((a, b) => a.depot.localeCompare(b.depot))
				}
			} catch (error) {
				toaster.error({ text: 'An error occurred retrieving the stock' })
				console.log('An error occurred retrieving the stock', error)
			} finally {
				loading.value = false
			}
		} else {
			loading.value = false
		}
	}

	// Watch for changes to the items prop (legacy mode)
	watch(
		() => props.items,
		() => {
			if (props.items && props.items.length > 0) {
				loadStock()
			}
		},
		{ deep: true, immediate: true }
	)

	// Watch for changes to stockItems prop (new generic mode)
	watch(
		() => props.stockItems,
		() => {
			if (props.stockItems && props.stockItems.length > 0) {
				loadStock()
			}
		},
		{ deep: true, immediate: true }
	)

	const getStockValue = (code, depot) => {
		const stockItem = stock.value.find((item) => item.depot === depot && item.grp_code === code)
		if (stockItem) {
			return stockStatuses.value.reduce((total, status) => {
				if (Object.prototype.hasOwnProperty.call(stockItem, status)) {
					total += stockItem[status] || 0
				}
				return total
			}, 0)
		}
		return 0
	}

	const depotStockClass = (code, quantity, depot) => {
		const stock = getStockValue(code, depot)
		return {
			'bg-green-300': stock && quantity <= stock,
			'bg-yellow-300': stock && quantity > stock && stock !== 0,
			'bg-red-300': !stock || stock <= 0
		}
	}

	const getStockColorClass = (code, quantity, depot) => {
		const stock = getStockValue(code, depot)
		if (stock && quantity <= stock) return 'text-green-700'
		if (stock && quantity > stock && stock !== 0) return 'text-orange-600'
		return 'text-gray-400'
	}

	const formatDistance = (totalDistance: number): string => {
		const miles = Math.floor(totalDistance)
		return `${miles} mi`
	}

	const emitShortageRequest = () => {
		console.log('emitShortageRequest called')
		console.log('current depots.value:', depots.value)
		console.log('current stock.value:', stock.value)
		console.log('loading.value:', loading.value)
		emit('shortageRequest')
	}

	// Expose data for parent component
	defineExpose({
		depots,
		stock,
		loading
	})
</script>
